import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAPR } from '../context/APRContext';

const Costing: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useAPR();
  const { dashboardStats, currentRunRateData } = state;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Calculate detailed statistics for each cost type
  const nonParentApps = currentRunRateData.filter((app: any) => !app.isParent && !app.isParentSummary);
  const totalApplicationsWithCosts = nonParentApps.length;
  const appsWithTCO = nonParentApps.filter((app: any) => app.totalAnnualCost > 0).length;
  const appsWithoutTCO = totalApplicationsWithCosts - appsWithTCO;

  // Current Run Rate Analysis (exclude parent apps to avoid double-counting)
  const portfolioCost = nonParentApps.reduce((sum: number, app: any) => sum + (app.totalAnnualCost || 0), 0);
  const currentRunRateStats = {
    total: portfolioCost,
    applications: totalApplicationsWithCosts,
    average: totalApplicationsWithCosts > 0 ? portfolioCost / totalApplicationsWithCosts : 0,
    highest: nonParentApps.reduce((max: any, app: any) => (!max || app.totalAnnualCost > max.totalAnnualCost) ? app : max, nonParentApps[0]),
    lowest: nonParentApps.reduce((min: any, app: any) => (!min || app.totalAnnualCost < min.totalAnnualCost) ? app : min, nonParentApps[0])
  };

  // Cost type breakdown calculations (exclude parent apps)
  const costBreakdown = {
    softwareLicensing: nonParentApps.reduce((sum: number, app: any) => sum + (app.annualSoftwareLicensingFees || 0), 0),
    vendorSupport: nonParentApps.reduce((sum: number, app: any) => sum + (app.annualThirdPartyVendorSupport || 0), 0),
    infrastructure: nonParentApps.reduce((sum: number, app: any) => sum + (app.annualInfrastructureCost || 0), 0),
    operational: nonParentApps.reduce((sum: number, app: any) => sum + (app.indirectOperationalCosts || 0), 0),
    cloudSaaS: nonParentApps.reduce((sum: number, app: any) => sum + (app.cloudSaasSubscriptionCostImpact || 0), 0)
  };

  // Calculate percentages for cost breakdown
  const totalBreakdown = Object.values(costBreakdown).reduce((sum, cost) => sum + cost, 0);
  
  // Projected savings analysis
  const totalCurrentCosts = portfolioCost;
  const totalFutureCosts = dashboardStats.totalCosts.future_run_rate;
  const totalSavings = totalCurrentCosts - totalFutureCosts;
  const savingsPercentage = totalCurrentCosts > 0 ? (totalSavings / totalCurrentCosts) * 100 : 0;

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header__content">
          <div className="page-header__title-section">
            <h1 className="page-header__title">Costing Analysis</h1>
            <p className="page-header__subtitle">
              Comprehensive cost analysis across all categories and projections
            </p>
          </div>
          <div className="page-header__actions">
          </div>
        </div>
      </div>

      {/* Main Content Container */}
      <div className="master-data-container">

      <div className="dashboard-grid">
        {/* Overview Statistics */}
        <div className="dashboard-section">
          <h2>Cost Overview</h2>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'nowrap' }}>
            <div className="overview-card" style={{ flex: 1, minWidth: 0 }}>
              <div className="card-header">
                <h3 style={{ fontSize: '13px' }}>Total Portfolio Cost</h3>
                <span className="card-icon">💰</span>
              </div>
              <div className="card-value" style={{ fontSize: '20px' }}>{formatCurrency(totalCurrentCosts)}</div>
              <div className="card-subtitle">Annual operational expenses</div>
            </div>
            <div className="overview-card" style={{ flex: 1, minWidth: 0 }}>
              <div className="card-header">
                <h3 style={{ fontSize: '13px' }}>Applications Analyzed</h3>
                <span className="card-icon">📱</span>
              </div>
              <div className="card-value" style={{ fontSize: '20px' }}>{totalApplicationsWithCosts}</div>
              <div className="card-subtitle">Total applications</div>
            </div>
            <div className="overview-card" style={{ flex: 1, minWidth: 0 }}>
              <div className="card-header">
                <h3 style={{ fontSize: '13px' }}>TCO Defined</h3>
                <span className="card-icon">✅</span>
              </div>
              <div className="card-value" style={{ fontSize: '20px', color: '#10b981' }}>{appsWithTCO}</div>
              <div className="card-subtitle">Apps with cost data</div>
            </div>
            <div className="overview-card" style={{ flex: 1, minWidth: 0 }}>
              <div className="card-header">
                <h3 style={{ fontSize: '13px' }}>TCO Missing</h3>
                <span className="card-icon">⚠️</span>
              </div>
              <div className="card-value" style={{ fontSize: '20px', color: appsWithoutTCO > 0 ? '#dc2626' : '#10b981' }}>{appsWithoutTCO}</div>
              <div className="card-subtitle">Apps lacking cost data</div>
            </div>
            <div className="overview-card" style={{ flex: 1, minWidth: 0 }}>
              <div className="card-header">
                <h3 style={{ fontSize: '13px' }}>Average Cost per App</h3>
                <span className="card-icon">📊</span>
              </div>
              <div className="card-value" style={{ fontSize: '20px' }}>{formatCurrency(currentRunRateStats.average)}</div>
              <div className="card-subtitle">Mean annual expense</div>
            </div>
          </div>
        </div>

        {/* Cost Breakdown Analysis */}
        <div className="dashboard-section">
          <h2>Current Run Rate Cost Breakdown</h2>
          <div className="cost-grid">
            <div className="cost-card">
              <div className="cost-header">
                <h3>Software Licensing</h3>
                <span className="cost-icon">📄</span>
              </div>
              <div className="cost-value">{formatCurrency(costBreakdown.softwareLicensing)}</div>
              <div className="cost-subtitle">
                {totalBreakdown > 0 ? `${((costBreakdown.softwareLicensing / totalBreakdown) * 100).toFixed(1)}% of total` : '0%'}
              </div>
            </div>
            <div className="cost-card">
              <div className="cost-header">
                <h3>Vendor Support</h3>
                <span className="cost-icon">🛠️</span>
              </div>
              <div className="cost-value">{formatCurrency(costBreakdown.vendorSupport)}</div>
              <div className="cost-subtitle">
                {totalBreakdown > 0 ? `${((costBreakdown.vendorSupport / totalBreakdown) * 100).toFixed(1)}% of total` : '0%'}
              </div>
            </div>
            <div className="cost-card">
              <div className="cost-header">
                <h3>Infrastructure</h3>
                <span className="cost-icon">🏗️</span>
              </div>
              <div className="cost-value">{formatCurrency(costBreakdown.infrastructure)}</div>
              <div className="cost-subtitle">
                {totalBreakdown > 0 ? `${((costBreakdown.infrastructure / totalBreakdown) * 100).toFixed(1)}% of total` : '0%'}
              </div>
            </div>
            <div className="cost-card">
              <div className="cost-header">
                <h3>Operational Costs</h3>
                <span className="cost-icon">⚙️</span>
              </div>
              <div className="cost-value">{formatCurrency(costBreakdown.operational)}</div>
              <div className="cost-subtitle">
                {totalBreakdown > 0 ? `${((costBreakdown.operational / totalBreakdown) * 100).toFixed(1)}% of total` : '0%'}
              </div>
            </div>
            <div className="cost-card">
              <div className="cost-header">
                <h3>Cloud/SaaS</h3>
                <span className="cost-icon">☁️</span>
              </div>
              <div className="cost-value">{formatCurrency(costBreakdown.cloudSaaS)}</div>
              <div className="cost-subtitle">
                {totalBreakdown > 0 ? `${((costBreakdown.cloudSaaS / totalBreakdown) * 100).toFixed(1)}% of total` : '0%'}
              </div>
            </div>
          </div>
        </div>

        {/* Cost Extremes Analysis */}
        <div className="dashboard-section">
          <h2>Cost Distribution Analysis</h2>
          <div className="card-grid">
            <div className="overview-card">
              <div className="card-header">
                <h3>Highest Cost Application</h3>
                <span className="card-icon">📈</span>
              </div>
              <div className="card-value">{formatCurrency(currentRunRateStats.highest?.totalAnnualCost || 0)}</div>
              <div className="card-subtitle">{currentRunRateStats.highest?.applicationName || 'N/A'}</div>
            </div>
            <div className="overview-card">
              <div className="card-header">
                <h3>Lowest Cost Application</h3>
                <span className="card-icon">📉</span>
              </div>
              <div className="card-value">{formatCurrency(currentRunRateStats.lowest?.totalAnnualCost || 0)}</div>
              <div className="card-subtitle">{currentRunRateStats.lowest?.applicationName || 'N/A'}</div>
            </div>
            <div className="overview-card">
              <div className="card-header">
                <h3>Cost Range</h3>
                <span className="card-icon">📏</span>
              </div>
              <div className="card-value">
                {formatCurrency((currentRunRateStats.highest?.totalAnnualCost || 0) - (currentRunRateStats.lowest?.totalAnnualCost || 0))}
              </div>
              <div className="card-subtitle">Difference between extremes</div>
            </div>
            <div className="overview-card">
              <div className="card-header">
                <h3>Cost Variance</h3>
                <span className="card-icon">📊</span>
              </div>
              <div className="card-value">
                {currentRunRateStats.highest?.totalAnnualCost && currentRunRateStats.average > 0 
                  ? `${((currentRunRateStats.highest.totalAnnualCost / currentRunRateStats.average) * 100 - 100).toFixed(0)}%`
                  : '0%'
                }
              </div>
              <div className="card-subtitle">Highest vs average</div>
            </div>
          </div>
        </div>

        {/* Future Cost Projections */}
        <div className="dashboard-section">
          <h2>Cost Projections & Investment</h2>
          <div className="cost-grid">
            <div className="cost-card">
              <div className="cost-header">
                <h3>Current Run Rate</h3>
                <span className="cost-icon">💰</span>
              </div>
              <div className="cost-value">{formatCurrency(portfolioCost)}</div>
              <div className="cost-subtitle">Baseline annual costs</div>
              <div className="cost-actions">
                <button 
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => navigate('/customers/costing/current-run-rate')}
                >
                  View Details →
                </button>
              </div>
            </div>
            <div className="cost-card">
              <div className="cost-header">
                <h3>Planned Investment</h3>
                <span className="cost-icon">📋</span>
              </div>
              <div className="cost-value">{formatCurrency(dashboardStats.totalCosts.planned_cost)}</div>
              <div className="cost-subtitle">One-time improvement projects</div>
              <div className="cost-actions">
                <button 
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => navigate('/customers/costing/planned-projects')}
                >
                  View Details →
                </button>
              </div>
            </div>
            <div className="cost-card">
              <div className="cost-header">
                <h3>Migration & Conversion</h3>
                <span className="cost-icon">🔄</span>
              </div>
              <div className="cost-value">{formatCurrency(dashboardStats.totalCosts.migration_conversion)}</div>
              <div className="cost-subtitle">Rationalization implementation</div>
              <div className="cost-actions">
                <button 
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => navigate('/customers/costing/migration-conversion')}
                >
                  View Details →
                </button>
              </div>
            </div>
            <div className="cost-card">
              <div className="cost-header">
                <h3>Future Run Rate</h3>
                <span className="cost-icon">🎯</span>
              </div>
              <div className="cost-value">{formatCurrency(dashboardStats.totalCosts.future_run_rate)}</div>
              <div className="cost-subtitle">Post-optimization annual costs</div>
              <div className="cost-actions">
                <button 
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => navigate('/customers/costing/future-run-rates')}
                >
                  View Details →
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>
      </div>
    </div>
  );
};

export default Costing;